/**
 * auto-stop-dialog.tsx — Hộp "Tự động ngừng trả lời" của nhân viên AI.
 *
 *   Đầu hộp: khung giới thiệu + công tắc (tắt → 2 thẻ bên dưới mờ, vẫn xem được).
 *   Thẻ "Cài đặt điều kiện dừng": các nhóm điều kiện — trong nhóm nối "Và", giữa các nhóm nối "Hoặc";
 *     "+ Tạo" mở bảng chọn 2 cột (Truy vấn: khách hàng / tuỳ chỉnh / hệ thống → tìm + chọn thuộc tính →
 *     phép so sánh → giá trị) → thành chip. Dưới: Lời kết (≤500) + Hình ảnh kết thúc (tải ảnh lên).
 *   Thẻ "Cài đặt thời gian dừng": các khung thời gian (lặp lại hằng tuần / hằng ngày / ngày cụ thể, múi giờ,
 *     bắt đầu → kết thúc, hỗ trợ qua nửa đêm).
 *   Chân: Huỷ / Xác nhận — kiểm lỗi hiển thị ngay dưới từng chỗ.
 *
 * Sửa trên bản sao; Xác nhận mới ghi vào form nháp (rồi tự lưu). Hàm thuần ở auto-stop-lib.ts.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Clock, Globe, ImagePlus, Loader2, OctagonPause, Plus, Search, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch, Textarea } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { apiError } from '@/lib/api-client'
import { useUploadProductImage } from '@/hooks/use-products'
import type { AutoStopNotice } from '@/hooks/use-ai-train-test'
import { avatarSrc } from '@/pages/settings/settings-utils'
import { TagInput } from './train-ui'
import { InfoTip } from './settings-row'
import { useVariableCatalog, withLiveAttributes, type CatalogVariable } from './train-api'
import type { AutoStopSchedule, AutoStopSettings, CollectAttribute, Condition, ConditionOp } from './bot-settings'
import {
  AUTO_STOP_LIMITS, DEFAULT_TZ, REPEAT_OPTIONS, STOP_OP_HINTS, STOP_OP_LABELS, WEEKDAY_NAMES,
  buildPickerSections, cleanAutoStop, coerceValue, conditionChip, hasErrors, newSchedule, scheduleNote,
  searchTimezones, searchVariables, stopConditionProblem, timezoneLabel, timezoneOptions, validateAutoStop, valueKind,
  type AutoStopErrors, type PickerSection, type PickerSectionId,
} from './auto-stop-lib'

type TabKey = 'conditions' | 'schedules'

export function AutoStopDialog({ open, onOpenChange, value, onSave, botId, attributes, canEdit }: {
  open: boolean
  onOpenChange: (o: boolean) => void
  value: AutoStopSettings
  onSave: (v: AutoStopSettings) => void
  botId: string
  attributes: CollectAttribute[]
  canEdit: boolean
}) {
  const { t } = useTranslation('aiTrainUi')
  const [draft, setDraft] = useState<AutoStopSettings>(value)
  const [tab, setTab] = useState<TabKey>('conditions')
  // Lỗi chỉ hiện sau lần bấm Xác nhận đầu tiên, rồi cập nhật theo lúc sửa.
  const [showErrors, setShowErrors] = useState(false)
  useEffect(() => {
    if (!open) return
    setDraft(value)
    setShowErrors(false)
    setTab(value.conditionGroups.length === 0 && value.schedules.length > 0 ? 'schedules' : 'conditions')
  }, [open, value])

  const { data } = useVariableCatalog(open ? botId : undefined)
  const cat = withLiveAttributes(data, attributes)
  const sections = useMemo(() => buildPickerSections(cat), [cat])
  const allVars = useMemo(() => sections.flatMap((s) => s.variables), [sections])

  const upd = (fn: (d: AutoStopSettings) => AutoStopSettings) => setDraft((d) => fn(d))
  const errors: AutoStopErrors | null = showErrors ? validateAutoStop(draft) : null
  const off = !draft.enabled
  const locked = !canEdit || off

  const confirm = () => {
    const e = validateAutoStop(draft)
    if (hasErrors(e)) {
      setShowErrors(true)
      if (Object.keys(e.schedules).length && !Object.keys(e.conditions).length) setTab('schedules')
      else if (Object.keys(e.conditions).length || e.closing) setTab('conditions')
      return
    }
    onSave(cleanAutoStop(draft))
    onOpenChange(false)
  }

  const condErrCount = errors ? Object.keys(errors.conditions).length + (errors.closing ? 1 : 0) : 0
  const schErrCount = errors ? Object.keys(errors.schedules).length : 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><OctagonPause className="h-4 w-4 text-primary" /> {t('autoStop.title')}</DialogTitle>
          <DialogDescription className="sr-only">{t('autoStop.srDescription')}</DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">{t('autoStop.title')}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{t('autoStop.intro')}</p>
          </div>
          <Switch checked={draft.enabled} onCheckedChange={(on) => upd((d) => ({ ...d, enabled: on }))} disabled={!canEdit} aria-label={t('autoStop.enableAria')} />
        </div>
        {errors?.general && <p className="text-sm text-destructive">{errors.general}</p>}

        <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
          <TabsList className={cn('w-full', off && 'opacity-60')}>
            <TabsTrigger value="conditions" className="flex-1 gap-1.5">
              {t('autoStop.tabConditions')} {condErrCount > 0 && <span className="rounded-full bg-destructive px-1.5 text-[10px] text-destructive-foreground">{condErrCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="schedules" className="flex-1 gap-1.5">
              {t('autoStop.tabSchedules')} {schErrCount > 0 && <span className="rounded-full bg-destructive px-1.5 text-[10px] text-destructive-foreground">{schErrCount}</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="conditions" className={cn('mt-3 space-y-3', off && 'opacity-50')}>
            <p className="text-xs text-muted-foreground">
              {t('autoStop.conditionsIntro')}
              <InfoTip text={t('autoStop.conditionsInfo')} />
            </p>
            <ConditionGroups
              groups={draft.conditionGroups} sections={sections} allVars={allVars} disabled={locked} errors={errors?.conditions ?? {}}
              onChange={(g) => upd((d) => ({ ...d, conditionGroups: g }))}
            />
            <div className="border-t pt-3" />
            <ClosingFields draft={draft} upd={upd} disabled={locked} error={errors?.closing} />
          </TabsContent>

          <TabsContent value="schedules" className={cn('mt-3 space-y-3', off && 'opacity-50')}>
            <p className="text-xs text-muted-foreground">
              {t('autoStop.schedulesIntro')}
              <InfoTip text={t('autoStop.schedulesInfo')} />
            </p>
            <SchedulesEditor schedules={draft.schedules} onChange={(s) => upd((d) => ({ ...d, schedules: s }))} disabled={locked} errors={errors?.schedules ?? {}} />
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{canEdit ? t('common:actions.cancel') : t('common:actions.close')}</Button>
          {canEdit && <Button onClick={confirm}>{t('common:actions.confirm')}</Button>}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ══════════════════════ Nhóm điều kiện ══════════════════════

function ConditionGroups({ groups, sections, allVars, onChange, disabled, errors }: {
  groups: Array<{ conditions: Condition[] }>
  sections: PickerSection[]
  allVars: CatalogVariable[]
  onChange: (g: Array<{ conditions: Condition[] }>) => void
  disabled: boolean
  errors: Record<string, string>
}) {
  const { t } = useTranslation('aiTrainUi')
  const setGroup = (gi: number, conds: Condition[]) => onChange(groups.map((g, i) => (i === gi ? { conditions: conds } : g)))
  return (
    <div className="space-y-0">
      {groups.length === 0 && (
        <p className="rounded-lg border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">{t('autoStop.noGroups')}</p>
      )}
      {groups.map((g, gi) => (
        <div key={gi}>
          {gi > 0 && (
            <div className="relative flex justify-center py-2">
              <span className="absolute inset-x-0 top-1/2 border-t border-dashed" />
              <span className="relative rounded-full bg-primary px-3 py-0.5 text-xs font-medium text-primary-foreground">{t('autoStop.or')}</span>
            </div>
          )}
          <div className="rounded-lg border">
            <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-1.5">
              <span className="text-sm font-medium">{t('autoStop.condition')} {groups.length > 1 && <span className="text-muted-foreground">{gi + 1}</span>}</span>
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={disabled} onClick={() => onChange(groups.filter((_, i) => i !== gi))} aria-label={t('autoStop.removeGroup')}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2 p-3">
              {g.conditions.length === 0 && <span className="text-xs text-muted-foreground">{t('autoStop.emptyGroup')}</span>}
              {g.conditions.map((c, ci) => {
                const v = allVars.find((x) => x.id === c.variable)
                const err = errors[`${gi}:${ci}`]
                return (
                  <span key={ci} className="contents">
                    {ci > 0 && <span className="text-xs font-semibold text-muted-foreground">{t('autoStop.and')}</span>}
                    <ConditionPopover
                      sections={sections} initial={c} disabled={disabled}
                      onSubmit={(nc) => setGroup(gi, g.conditions.map((x, j) => (j === ci ? nc : x)))}
                      trigger={
                        <span className={cn(
                          'inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
                          err ? 'border-destructive bg-destructive/10 text-destructive' : 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300',
                        )} title={err ?? t('autoStop.clickToEdit')}>
                          {conditionChip(c, v).map((part, k) => (
                            <span key={k} className={cn('truncate', k === 0 && 'font-medium')}>{k > 0 && <span className="mr-1.5 opacity-60">·</span>}{part}</span>
                          ))}
                          {!disabled && (
                            <button type="button" className="ml-0.5 rounded hover:bg-black/10" aria-label={t('autoStop.removeCondition')}
                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setGroup(gi, g.conditions.filter((_, j) => j !== ci)) }}>
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </span>
                      }
                    />
                  </span>
                )
              })}
              <ConditionPopover
                sections={sections} disabled={disabled || g.conditions.length >= AUTO_STOP_LIMITS.conditionsPerGroup}
                onSubmit={(nc) => setGroup(gi, [...g.conditions, nc])}
                trigger={
                  <Button type="button" size="sm" variant="outline" className="h-7 gap-1 border-dashed text-xs" disabled={disabled || g.conditions.length >= AUTO_STOP_LIMITS.conditionsPerGroup}>
                    <Plus className="h-3.5 w-3.5" /> {t('autoStop.create')}
                  </Button>
                }
              />
            </div>
            {g.conditions.some((_, ci) => errors[`${gi}:${ci}`]) && (
              <p className="px-3 pb-2 text-xs text-destructive">
                {g.conditions.map((_, ci) => errors[`${gi}:${ci}`] ? t('autoStop.conditionError', { n: ci + 1, error: errors[`${gi}:${ci}`] }) : null).filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
        </div>
      ))}
      <Button type="button" variant="ghost" className="mt-3 w-full gap-1 bg-primary/10 text-primary hover:bg-primary/15" disabled={disabled || groups.length >= AUTO_STOP_LIMITS.groups}
        onClick={() => onChange([...groups, { conditions: [] }])}>
        <Plus className="h-4 w-4" /> {t('autoStop.addGroup')}
      </Button>
    </div>
  )
}

/** Bảng chọn điều kiện (2 bước): chọn thuộc tính (2 cột) → phép so sánh + giá trị. */
function ConditionPopover({ sections, initial, onSubmit, trigger, disabled }: {
  sections: PickerSection[]
  initial?: Condition
  onSubmit: (c: Condition) => void
  trigger: React.ReactNode
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open && !disabled} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-[min(580px,92vw)] p-0">
        {open && <ConditionEditor sections={sections} initial={initial} onCancel={() => setOpen(false)} onSubmit={(c) => { onSubmit(c); setOpen(false) }} />}
      </PopoverContent>
    </Popover>
  )
}

function ConditionEditor({ sections, initial, onSubmit, onCancel }: {
  sections: PickerSection[]
  initial?: Condition
  onSubmit: (c: Condition) => void
  onCancel: () => void
}) {
  const { t } = useTranslation('aiTrainUi')
  const all = sections.flatMap((s) => s.variables)
  const initVar = initial ? all.find((v) => v.id === initial.variable) : undefined
  const [section, setSection] = useState<PickerSectionId>(() => sections.find((s) => s.variables.some((v) => v.id === initial?.variable))?.id ?? 'customer')
  const [query, setQuery] = useState('')
  const [variable, setVariable] = useState<CatalogVariable | undefined>(initVar)
  const [op, setOp] = useState<ConditionOp>(initial?.op ?? initVar?.operators[0] ?? '=')
  const [val, setVal] = useState<Condition['value']>(initial?.value)
  const [err, setErr] = useState<string | null>(null)

  const cur = sections.find((s) => s.id === section)
  const list = searchVariables(query ? all : cur?.variables ?? [], query)

  const pick = (v: CatalogVariable) => {
    const nop = v.operators.includes(op) && variable ? op : v.operators.includes('>=') && v.type === 'number' ? '>=' : v.operators[0]
    setVariable(v)
    setOp(nop)
    setVal(coerceValue(valueKind(v, nop), undefined))
    setErr(null)
  }

  if (!variable) {
    return (
      <div className="flex h-80">
        <div className="w-48 shrink-0 border-r bg-muted/30 p-2">
          <p className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t('autoStop.query')}</p>
          {sections.map((s) => (
            <button key={s.id} type="button" onClick={() => { setSection(s.id); setQuery('') }}
              className={cn('flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm', section === s.id && !query ? 'bg-background font-medium text-primary shadow-sm' : 'hover:bg-background/60')}>
              <span className="leading-snug">{s.label}</span>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-60" />
            </button>
          ))}
          {cur && <p className="mt-2 px-2 text-[11px] leading-snug text-muted-foreground">{cur.hint}</p>}
        </div>
        <div className="flex min-w-0 flex-1 flex-col p-2">
          <div className="relative mb-2">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input autoFocus className="h-8 pl-7 text-sm" placeholder={t('autoStop.searchContent')} value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {list.length === 0 && (
              <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                {query ? t('autoStop.noAttributeFound') : section === 'custom' ? t('autoStop.noCustomAttributes') : t('autoStop.loading')}
              </p>
            )}
            {list.map((v) => (
              <button key={v.id} type="button" onClick={() => pick(v)} className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted">
                <span className="truncate">{v.name}</span>
                {v.description && <span onClick={(e) => e.stopPropagation()}><InfoTip text={v.description} /></span>}
                <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{v.type === 'number' ? t('autoStop.typeNumber') : v.type === 'multi' ? t('autoStop.typeMulti') : v.options.length ? t('autoStop.typeChoice') : t('autoStop.typeText')}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const kind = valueKind(variable, op)
  const submit = () => {
    const c: Condition = { variable: variable.id, op, ...(kind === 'none' ? {} : { value: val }) }
    const p = stopConditionProblem(c)
    if (p) { setErr(p); return }
    onSubmit(c)
  }
  return (
    <div className="space-y-3 p-3">
      <div className="flex items-center gap-2">
        {!initial && (
          <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => setVariable(undefined)} aria-label={t('autoStop.pickOther')}><ChevronLeft className="h-4 w-4" /></Button>
        )}
        <span className="text-sm font-medium">{variable.name}</span>
        {variable.description && <InfoTip text={variable.description} />}
        {initial && <button type="button" className="ml-auto text-xs text-primary hover:underline" onClick={() => setVariable(undefined)}>{t('autoStop.changeAttribute')}</button>}
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t('autoStop.operator')} {STOP_OP_HINTS[op] && <InfoTip text={STOP_OP_HINTS[op]} />}</Label>
        <Select value={op} onValueChange={(o) => { const nop = o as ConditionOp; setOp(nop); setVal(coerceValue(valueKind(variable, nop), val)); setErr(null) }}>
          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
          <SelectContent>{variable.operators.map((o) => <SelectItem key={o} value={o}>{STOP_OP_LABELS[o]}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {kind !== 'none' && (
        <div className="space-y-1">
          <Label className="text-xs">{t('autoStop.value')}</Label>
          <ValueField kind={kind} variable={variable} value={val} onChange={(v) => { setVal(v); setErr(null) }} onEnter={submit} />
        </div>
      )}
      {err && <p className="text-xs text-destructive">{err}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>{t('common:actions.cancel')}</Button>
        <Button type="button" size="sm" onClick={submit}>{initial ? t('common:actions.update') : t('autoStop.addCondition')}</Button>
      </div>
    </div>
  )
}

function ValueField({ kind, variable, value, onChange, onEnter }: {
  kind: ReturnType<typeof valueKind>
  variable: CatalogVariable
  value: Condition['value']
  onChange: (v: Condition['value']) => void
  onEnter: () => void
}) {
  const { t } = useTranslation('aiTrainUi')
  if (kind === 'multi-select') {
    const list = Array.isArray(value) ? value : []
    return (
      <div className="max-h-44 space-y-0.5 overflow-y-auto rounded-md border p-1">
        {variable.options.map((o) => {
          const on = list.includes(o.value)
          return (
            <label key={o.value} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted">
              <input type="checkbox" checked={on} onChange={() => onChange(on ? list.filter((x) => x !== o.value) : [...list, o.value])} />
              {o.label}
            </label>
          )
        })}
      </div>
    )
  }
  if (kind === 'tags') {
    return <TagInput value={Array.isArray(value) ? value : []} onChange={(v) => onChange(v)} placeholder={t('autoStop.typeValues')} max={50} />
  }
  if (kind === 'select') {
    return (
      <Select value={String(value ?? '')} onValueChange={(v) => onChange(v)}>
        <SelectTrigger className="h-8"><SelectValue placeholder={t('autoStop.pickValue')} /></SelectTrigger>
        <SelectContent>{variable.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    )
  }
  const numeric = kind === 'number'
  return (
    <Input className="h-8" autoFocus type={numeric ? 'number' : 'text'} min={numeric ? 0 : undefined} maxLength={200}
      value={value == null || Array.isArray(value) ? '' : String(value)} placeholder={numeric ? t('autoStop.exampleNumber') : t('autoStop.enterValue')}
      onChange={(e) => onChange(numeric && e.target.value !== '' ? Number(e.target.value) : e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onEnter() } }} />
  )
}

// ══════════════════════ Lời kết + ảnh ══════════════════════

function ClosingFields({ draft, upd, disabled, error }: {
  draft: AutoStopSettings
  upd: (fn: (d: AutoStopSettings) => AutoStopSettings) => void
  disabled: boolean
  error?: string
}) {
  const { t } = useTranslation('aiTrainUi')
  const upload = useUploadProductImage()
  const fileRef = useRef<HTMLInputElement>(null)
  const len = draft.closingMessage.length
  const onFile = async (f: File | undefined) => {
    if (!f) return
    if (!/^image\/(jpeg|png|webp|gif)$/.test(f.type)) { toast.error(t('autoStop.imageTypes')); return }
    if (f.size > 5 * 1024 * 1024) { toast.error(t('autoStop.imageMax')); return }
    try {
      const url = await upload.mutateAsync(f)
      upd((d) => ({ ...d, closingImages: [...d.closingImages, url].slice(0, AUTO_STOP_LIMITS.images) }))
    } catch (e) {
      toast.error(t('autoStop.uploadFailed', { message: apiError(e) }))
    }
  }
  return (
    <>
      <div className="space-y-1">
        <Label className="flex items-center text-sm">
          {t('autoStop.closing')} <InfoTip text={t('autoStop.closingInfo')} />
        </Label>
        <div className="relative">
          <Textarea rows={3} maxLength={AUTO_STOP_LIMITS.closing} value={draft.closingMessage} disabled={disabled} className="pb-6"
            onChange={(e) => upd((d) => ({ ...d, closingMessage: e.target.value }))}
            placeholder={t('autoStop.closingPlaceholder')} />
          <span className={cn('pointer-events-none absolute bottom-1.5 right-2 text-[11px]', len >= AUTO_STOP_LIMITS.closing ? 'text-destructive' : 'text-muted-foreground')}>
            {len}/{AUTO_STOP_LIMITS.closing}
          </span>
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
      <div className="space-y-1">
        <Label className="flex items-center text-sm">
          {t('autoStop.closingImages')} <InfoTip text={t('autoStop.closingImagesInfo', { max: AUTO_STOP_LIMITS.images })} />
        </Label>
        <div className="flex flex-wrap gap-2">
          {draft.closingImages.map((u) => (
            <div key={u} className="group relative h-20 w-20 overflow-hidden rounded-md border bg-muted">
              <img src={avatarSrc(u)} alt={t('autoStop.closingImages')} className="h-full w-full object-cover" />
              {!disabled && (
                <button type="button" aria-label={t('autoStop.removeImage')} onClick={() => upd((d) => ({ ...d, closingImages: d.closingImages.filter((x) => x !== u) }))}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white opacity-80 hover:opacity-100">
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          {draft.closingImages.length < AUTO_STOP_LIMITS.images && (
            <button type="button" disabled={disabled || upload.isPending} onClick={() => fileRef.current?.click()} aria-label={t('autoStop.uploadImage')}
              className="flex h-20 w-20 items-center justify-center rounded-md border border-dashed text-muted-foreground hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60">
              {upload.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
            onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = '' }} />
        </div>
      </div>
    </>
  )
}

// ══════════════════════ Khung thời gian dừng ══════════════════════

function SchedulesEditor({ schedules, onChange, disabled, errors }: {
  schedules: AutoStopSchedule[]
  onChange: (s: AutoStopSchedule[]) => void
  disabled: boolean
  errors: Record<number, string>
}) {
  const { t } = useTranslation('aiTrainUi')
  const tzList = useMemo(() => timezoneOptions(), [])
  const upd = (i: number, patch: Partial<AutoStopSchedule>) => onChange(schedules.map((s, j) => (j === i ? { ...s, ...patch } : s)))
  return (
    <div className="space-y-3">
      {schedules.length === 0 && (
        <p className="rounded-lg border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">{t('autoStop.noSchedules')}</p>
      )}
      {schedules.map((s, i) => {
        const note = scheduleNote(s)
        return (
          <div key={i} className={cn('space-y-2.5 rounded-lg border p-3', errors[i] && 'border-destructive/60')}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-16 shrink-0 text-xs text-muted-foreground">{t('autoStop.repeat')}</span>
              <Select value={s.repeat} disabled={disabled} onValueChange={(r) => upd(i, { repeat: r as AutoStopSchedule['repeat'] })}>
                <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
                <SelectContent>{REPEAT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              {s.repeat === 'weekly' && <DaysPicker days={s.days} onChange={(days) => upd(i, { days })} disabled={disabled} />}
              {s.repeat === 'dates' && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <Input type="date" className="h-8 w-40" value={s.dateFrom ?? ''} disabled={disabled} aria-label={t('autoStop.dateFrom')}
                    onChange={(e) => upd(i, { dateFrom: e.target.value, dateTo: s.dateTo && s.dateTo >= e.target.value ? s.dateTo : e.target.value })} />
                  <span className="text-xs text-muted-foreground">{t('autoStop.dateTo')}</span>
                  <Input type="date" className="h-8 w-40" value={s.dateTo ?? ''} min={s.dateFrom} disabled={disabled} aria-label={t('autoStop.dateToLabel')}
                    onChange={(e) => upd(i, { dateTo: e.target.value })} />
                </div>
              )}
              <Button size="icon" variant="ghost" className="ml-auto h-7 w-7" disabled={disabled} onClick={() => onChange(schedules.filter((_, j) => j !== i))} aria-label={t('autoStop.removeSchedule')}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-16 shrink-0 text-xs text-muted-foreground">{t('autoStop.timezone')}</span>
              <TimezonePicker value={s.timezone} options={tzList} onChange={(timezone) => upd(i, { timezone })} disabled={disabled} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-16 shrink-0 text-xs text-muted-foreground">{t('autoStop.start')}</span>
              <div className="relative">
                <Clock className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input type="time" className="h-8 w-32 pl-7" value={s.start} disabled={disabled} aria-label={t('autoStop.startTime')} onChange={(e) => upd(i, { start: e.target.value })} />
              </div>
              <span className="text-xs text-muted-foreground">{t('common:time.to')}</span>
              <div className="relative">
                <Clock className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input type="time" className="h-8 w-32 pl-7" value={s.end} disabled={disabled} aria-label={t('autoStop.endTime')} onChange={(e) => upd(i, { end: e.target.value })} />
              </div>
              <span className="text-xs text-muted-foreground">{t('autoStop.end')}</span>
            </div>
            {note && <p className="text-[11px] text-muted-foreground">{note}</p>}
            {errors[i] && <p className="text-xs text-destructive">{errors[i]}</p>}
          </div>
        )
      })}
      <Button type="button" size="sm" variant="outline" className="gap-1 border-dashed" disabled={disabled || schedules.length >= AUTO_STOP_LIMITS.schedules}
        onClick={() => onChange([...schedules, newSchedule(schedules.at(-1)?.timezone ?? DEFAULT_TZ)])}>
        <Plus className="h-3.5 w-3.5" /> {t('autoStop.create')}
      </Button>
    </div>
  )
}

function DaysPicker({ days, onChange, disabled }: { days: number[]; onChange: (d: number[]) => void; disabled: boolean }) {
  const { t } = useTranslation('aiTrainUi')
  const sorted = WEEKDAY_NAMES.filter((d) => days.includes(d.value))
  const toggle = (v: number) => onChange(days.includes(v) ? days.filter((x) => x !== v) : [...days, v])
  return (
    <div className="flex min-h-8 min-w-[220px] flex-1 flex-wrap items-center gap-1 rounded-md border px-1.5 py-1">
      {sorted.length === 0 && <span className="px-1 text-xs text-muted-foreground">{t('autoStop.noDays')}</span>}
      {sorted.map((d) => (
        <span key={d.value} className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
          {d.label}
          {!disabled && <button type="button" aria-label={t('autoStop.removeDay', { day: d.label })} onClick={() => toggle(d.value)}><X className="h-3 w-3" /></button>}
        </span>
      ))}
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" size="sm" variant="ghost" className="ml-auto h-6 px-1.5 text-xs" disabled={disabled}><Plus className="h-3 w-3" /> {t('autoStop.pickDays')}</Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-52 p-1">
          <div className="flex gap-1 border-b p-1 pb-1.5">
            <button type="button" className="rounded px-1.5 py-0.5 text-[11px] text-primary hover:bg-muted" onClick={() => onChange([1, 2, 3, 4, 5])}>{t('autoStop.weekdays')}</button>
            <button type="button" className="rounded px-1.5 py-0.5 text-[11px] text-primary hover:bg-muted" onClick={() => onChange([6, 0])}>{t('autoStop.weekend')}</button>
            <button type="button" className="rounded px-1.5 py-0.5 text-[11px] text-primary hover:bg-muted" onClick={() => onChange([0, 1, 2, 3, 4, 5, 6])}>{t('autoStop.wholeWeek')}</button>
          </div>
          {WEEKDAY_NAMES.map((d) => (
            <label key={d.value} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted">
              <input type="checkbox" checked={days.includes(d.value)} onChange={() => toggle(d.value)} /> {d.label}
            </label>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  )
}

function TimezonePicker({ value, options, onChange, disabled }: {
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (tz: string) => void
  disabled: boolean
}) {
  const { t } = useTranslation('aiTrainUi')
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const list = searchTimezones(options, q).slice(0, 200)
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ('') }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="h-8 min-w-[260px] justify-start gap-1.5 font-normal" disabled={disabled}>
          <Globe className="h-3.5 w-3.5 text-muted-foreground" /> <span className="truncate">{timezoneLabel(value)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2">
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus className="h-8 pl-7 text-sm" placeholder={t('autoStop.searchTimezone')} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {list.length === 0 && <p className="py-4 text-center text-xs text-muted-foreground">{t('autoStop.noTimezone')}</p>}
          {list.map((o) => (
            <button key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false); setQ('') }}
              className={cn('block w-full truncate rounded px-2 py-1.5 text-left text-sm hover:bg-muted', o.value === value && 'bg-primary/10 font-medium text-primary')}>
              {o.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ══════════════════════ Chat thử ══════════════════════

/** Chat thử: bong bóng lời kết / ảnh (nếu vừa ngừng) + dòng hệ thống "AI đã ngừng trả lời: <lý do>". */
export function AutoStopNoticeView({ notice, botEmoji }: { notice: AutoStopNotice; botEmoji: string }) {
  const { t } = useTranslation('aiTrainUi')
  return (
    <>
      {notice.closingMessage && (
        <div className="flex justify-start">
          <div className="max-w-[85%] whitespace-pre-wrap rounded-lg border bg-background px-3 py-2 text-sm" title={t('autoStop.closingTitle')}>
            <span className="mr-1">{botEmoji}</span>{notice.closingMessage}
          </div>
        </div>
      )}
      {notice.closingImages.length > 0 && (
        <div className="flex flex-wrap justify-start gap-1.5">
          {notice.closingImages.map((u) => (
            <img key={u} src={avatarSrc(u)} alt={t('autoStop.closingImages')} className="h-24 w-24 rounded-lg border object-cover" />
          ))}
        </div>
      )}
      <p className="flex items-center justify-center gap-1 text-center text-xs italic text-muted-foreground">
        <OctagonPause className="h-3 w-3" />
        {t('autoStop.stopped', { reason: notice.reason })}
        {notice.kind === 'already' && t('autoStop.alreadyStopped')}
      </p>
    </>
  )
}
